import type { Question } from '../../types';
import { isOmittableBlankDefault } from '../../unansweredValue';
import { flattenQuestionTree } from '../../walkQuestionTree';

export interface RadioOptionSchemaMismatch {
  qid: string;
  missingValues: string[];
  extraValues: string[];
}

/**
 * Compares partial radio layout values (`radio-option`) vs RogerDoc `values[]`.
 *
 * Skips questions with no partial declarations (e.g. `OASISRadio`-only layouts).
 * `OASISYesNoCheckbox` should be folded in via
 * `collectLayoutPartialRadioValuesByQid` before calling this function.
 * Uses full schema `values` (no `ignored_values` / visit filtering).
 */
export function computeRadioOptionSchemaMismatches(
  sectionQuestions: Question[],
  radioOptionValuesByQid: ReadonlyMap<string, ReadonlySet<string>>
): RadioOptionSchemaMismatch[] {
  const issues: RadioOptionSchemaMismatch[] = [];

  for (const q of flattenQuestionTree(sectionQuestions)) {
    if (q.type !== 'radio') continue;
    const schemaValues = q.values;
    if (!schemaValues || schemaValues.length === 0) continue;

    const layoutVals = radioOptionValuesByQid.get(q.qid);
    if (!layoutVals || layoutVals.size === 0) continue;

    const schemaSet = new Set(schemaValues);
    const missingValues = schemaValues.filter(sv => {
      if (layoutVals.has(sv)) return false;
      return !isOmittableBlankDefault(q, sv);
    });
    const extraValues = [...layoutVals]
      .filter(lv => !schemaSet.has(lv))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    if (missingValues.length > 0 || extraValues.length > 0) {
      issues.push({ qid: q.qid, missingValues, extraValues });
    }
  }

  issues.sort((a, b) => a.qid.localeCompare(b.qid));
  return issues;
}
